import { useState } from "react";
import { toast } from "sonner";
import { MapPin, MessageCircle, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Reveal from "@/components/site/Reveal";

export default function Contact({ projects }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", project: "", message: "", visit_date: "", visit_time: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! We will reach out to you shortly.");
      setForm({ name: "", phone: "", email: "", project: "", message: "", visit_date: "", visit_time: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-black border border-white/15 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 transition-colors duration-300";

  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="contact-section">
      <div className="grid lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="font-accent text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">05 — Begin</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="contact-title">
            Your Dream Address
            <br />
            <span className="gold-text italic">Awaits.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base leading-relaxed max-w-md">
            Tell us what you're looking for — a home, an investment, a retail space. Our team in Dahod will call you back.
          </p>
          <div className="mt-10 flex items-center gap-3 text-zinc-300" data-testid="contact-location">
            <span className="w-10 h-10 border border-[#C5A059]/40 flex items-center justify-center">
              <MapPin size={16} className="text-[#C5A059]" />
            </span>
            <div>
              <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500">Visit Us</p>
              <p className="text-sm mt-1">Dahod, Gujarat, India</p>
            </div>
          </div>
          <a
            href="https://wa.me/917057970705?text=Hi%2C%20I%20found%20you%20on%20your%20website%20and%20would%20like%20to%20enquire%20about%20your%20services."
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 flex items-center gap-3 text-zinc-300 group w-fit"
            data-testid="whatsapp-button"
          >
            <span className="w-10 h-10 border border-[#C5A059]/40 flex items-center justify-center group-hover:bg-[#C5A059] transition-colors duration-300">
              <MessageCircle size={16} className="text-[#C5A059] group-hover:text-black transition-colors duration-300" />
            </span>
            <div>
              <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500">Chat With Us</p>
              <p className="text-sm mt-1 group-hover:text-[#C5A059] transition-colors duration-300">WhatsApp: +91 70579 70705</p>
            </div>
          </a>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="border border-[#C5A059]/20 bg-[#0A0A0A] p-8 lg:p-10 space-y-5" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required placeholder="Your Name" value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" />
              <input required placeholder="Phone Number" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="enquiry-phone-input" />
            </div>
            <input type="email" placeholder="Email Address (optional — for our thank-you note)" value={form.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email-input" />
            <select value={form.project} onChange={set("project")} className={`${inputCls} appearance-none`} data-testid="enquiry-project-select">
              <option value="">Interested Project (optional)</option>
              {projects.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
            <textarea rows={4} placeholder="Your Message" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="enquiry-message-input" />
            <div className="grid sm:grid-cols-2 gap-5 border border-white/10 p-5">
              <div>
                <label className="block font-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Preferred Visit Date (optional)</label>
                <input type="date" min={new Date().toISOString().split("T")[0]} value={form.visit_date} onChange={set("visit_date")} style={{ colorScheme: "dark" }} className={inputCls} data-testid="enquiry-visit-date-input" />
              </div>
              <div>
                <label className="block font-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Preferred Time (optional)</label>
                <input type="time" value={form.visit_time} onChange={set("visit_time")} style={{ colorScheme: "dark" }} className={inputCls} data-testid="enquiry-visit-time-input" />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="w-full bg-[#C5A059] text-black font-accent text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-[#D4AF37] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {sending ? "Sending..." : "Send Enquiry"} <Send size={14} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
